import { ComparisonTable } from '@/components/astro/new-design-25/SEO/comparison-table';
import { FeatureHighlight } from '@/components/astro/new-design-25/SEO/FeatureHighlight';
import { LinksSrOnly } from '@/components/astro/new-design-25/SEO/LinksSrOnly';
import { SeoContent } from '@/components/astro/new-design-25/SEO/seo-content';
import { SeoHero } from '@/components/astro/new-design-25/SEO/seo-hero';
import { SeoSlider } from '@/components/astro/new-design-25/SEO/seo-slider.tsx';
import { SolutionBenefits } from '@/components/astro/new-design-25/SEO/solution-benefits';
import { UseCases } from '@/components/astro/new-design-25/SEO/use-cases';
import { FAQButton } from '@/components/astro/new-design-25/ui/FAQ-Button';
import { Footer } from '@/components/astro/new-design-25/ui/Footer';
import { NavBar } from '@/components/astro/new-design-25/ui/NavBar';
import { Testimonials } from '@/components/astro/new-design-25/ui/Testimonials';
import { TryListeningBanner } from '@/components/astro/new-design-25/ui/TryListeningBanner';
import { GridBackground } from '@/components/shared-ui/grid-background';

type SlideData = {
  id: number;
  title: string;
  subtitle: string;
  backgroundGradient: string;
  numberGradient: string;
  problem: string;
  impact: string;
  solution: string;
  solutionDetail: string;
};

type UseCase = {
  title: string;
  challenge: string;
  solutionSections: Array<{
    label: string;
    content: string;
  }>;
};

type ComparisonColumn = {
  header: string;
  subheader?: string;
  isHighlighted?: boolean;
};

type ComparisonRow = {
  feature: string;
  description: string;
  values: Array<
    string | { text: string; icon?: 'check' | 'close' | 'warning' }
  >;
};

type BenefitItem = {
  text: string;
  highlight?: string;
};

type Props = {
  pageTitle?: string;
  pageDescription?: string;
  featureTitle?: string;
  featureSubtitle?: string;
  featureDescription?: string;
  featureFeatures?: Array<{
    title: string;
    description: string;
  }>;
  asideFeatures?: Array<{
    title: string;
    description?: string;
  }>;
  featureCtaText?: string;
  featureCtaHref?: string;
  featureBadge?: string;
  featureVariant?: 'default' | 'gradient';
  seoSections?: Array<{
    title: string;
    content: string;
    contentSecondary?: string;
  }>;
  // Slider props
  sliderData?: {
    slides: Array<SlideData>;
    title?: string;
    description?: string;
    autoplayDelay?: number;
  };
  // Solution Benefits props
  solutionBenefitsData?: {
    title: string;
    items: Array<BenefitItem>;
  };
  // Use Cases props
  useCasesData?: {
    title: string;
    description: string;
    useCases: Array<UseCase>;
  };
  // Comparison Table props
  comparisonTableData?: {
    title: string;
    description: string;
    columns: Array<ComparisonColumn>;
    rows: Array<ComparisonRow>;
    showIcons?: boolean;
    schemaType?: string;
    ariaLabel?: string;
  };
};

export function SeoLayout(props: Props) {
  const {
    pageTitle,
    pageDescription,
    featureTitle,
    featureSubtitle,
    featureDescription,
    featureFeatures,
    featureCtaText,
    featureBadge,
    featureVariant,
    seoSections,
    sliderData,
    solutionBenefitsData,
    useCasesData,
    comparisonTableData,
    asideFeatures,
  } = props;

  return (
    <>
      <a
        className='sr-only focus:not-sr-only focus:absolute focus:z-50 focus:p-4'
        href='#main-content'
      >
        Skip to main content
      </a>
      <LinksSrOnly />
      <div className='relative'>
        <GridBackground>
          <div className='relative z-10'>
            <NavBar />
            <main id='main-content' tabIndex={-1}>
              <SeoHero
                pageDescription={pageDescription}
                pageTitle={pageTitle}
              />
            </main>
          </div>
        </GridBackground>
      </div>
      {featureTitle && (
        <FeatureHighlight
          asideFeatures={asideFeatures}
          badge={featureBadge}
          ctaText={featureCtaText}
          description={featureDescription}
          features={featureFeatures}
          subtitle={featureSubtitle}
          title={featureTitle}
          variant={featureVariant}
        />
      )}

      {sliderData && (
        <SeoSlider
          autoplayDelay={sliderData.autoplayDelay}
          description={sliderData.description}
          slides={sliderData.slides}
          title={sliderData.title}
        />
      )}

      {solutionBenefitsData && (
        <SolutionBenefits
          items={solutionBenefitsData.items}
          title={solutionBenefitsData.title}
        />
      )}

      {useCasesData && (
        <UseCases
          _useCases={useCasesData.useCases}
          description={useCasesData.description}
          title={useCasesData.title}
        />
      )}

      {comparisonTableData && (
        <ComparisonTable
          ariaLabel={comparisonTableData.ariaLabel}
          columns={comparisonTableData.columns}
          description={comparisonTableData.description}
          rows={comparisonTableData.rows}
          schemaType={comparisonTableData.schemaType}
          showIcons={comparisonTableData.showIcons}
          title={comparisonTableData.title}
        />
      )}

      {seoSections && <SeoContent sections={seoSections} />}

      <Testimonials />
      <div
        className='bg-linear-to-b from-azure-525/5 to-azure-525/20'
        role='complementary'
      >
        <FAQButton />
        <TryListeningBanner />
        <Footer />
      </div>
    </>
  );
}
